import React from 'react';
import { Avatar, Box, Container, Divider, Grid } from '@mui/material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import { Link, useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Section from 'components/common/Section/Section';
import SectionSkeleton from 'components/skeletons/SectionSkeleton';
import ChatWidget from 'components/common/ChatWidget/ChatWidget';
import ProductInfoItem from 'components/Product/ProductInfoItem/ProductInfoItem';
import CustomText from 'components/common/CustomText/CustomText';
import { useGetStoreProduct } from 'hooks/useStore';
import { currencies } from 'common/constants';
import { dateToString } from 'common/utils';
import { colors } from 'styles/colors';
import useGlobalStyles from 'styles/globalStyles';

// A field with `to` shows its value as a link that opens that page in a new tab.
type Field = { label: string; value?: string | null; to?: string };

// Label and value pairs two to a row, the way the product page lays them out.
function Fields({ fields }: { fields: Field[] }) {
  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.label}>
          <Grid size={{ xs: 12, md: 6 }}>
            {field.to && field.value ? (
              <Box>
                <CustomText text={field.label} fontSize={15} fontWeight={600} />
                <Link to={field.to} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <CustomText text={field.value} fontSize={15} fontWeight={500} color={colors.link} />
                </Link>
              </Box>
            ) : (
              <ProductInfoItem label={field.label} value={field.value} />
            )}
          </Grid>

          {index % 2 === 1 && index < fields.length - 1 && (
            <Grid size={12}>
              <Divider />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

// A product as one store sells it. What the product is lives on its own page,
// which this links to; what is here is what this store decides about it.
export default function StoreProductDetailsPage() {
  const globalStyles = useGlobalStyles();
  const params = useParams();
  const storeId = String(params.storeId);
  const storeProductId = String(params.storeProductId);

  const { data: storeProduct } = useGetStoreProduct(storeId, storeProductId, {
    select: (res: any) => res.data,
  }) as { data: any };

  if (!storeProduct) {
    return (
      <Box className={globalStyles.container} p={3}>
        <SectionSkeleton count={4} />
        <Box mt={3}>
          <SectionSkeleton count={6} />
        </Box>
      </Box>
    );
  }

  const product = storeProduct.product;
  const store = storeProduct.store;
  const weight = product.weight ?? product.estimatedWeight;

  const storeFields: Field[] = [
    { label: 'Store', value: store?.name, to: `/stores/all-stores/${storeId}` },
    { label: 'Price', value: `${currencies.shekel}${Number(storeProduct.price).toFixed(2)}` },
    { label: 'Quantity', value: String(storeProduct.quantity) },
    { label: 'Max Per Order', value: String(storeProduct.maxQuantityPerOrder) },
    { label: 'Added On', value: storeProduct.createdAt ? dateToString(new Date(storeProduct.createdAt.split('T')[0])) : null },
  ];

  const productFields: Field[] = [
    { label: 'English Name', value: product.names?.en },
    { label: 'Arabic Name', value: product.names?.ar },
    { label: 'Barcode', value: product.barcode },
    { label: 'Brand', value: product.brand?.nameEn ?? product.brand?.nameAr },
    { label: 'Category', value: product.category?.nameEn ?? product.category?.nameAr },
    { label: 'Weight', value: weight ? `${product.weight ? '' : 'about '}${weight}${product.weightUnit ?? ''}` : null },
    { label: 'Published', value: product.publishedAt ? dateToString(new Date(product.publishedAt.split('T')[0])) : 'No' },
  ];

  return (
    <>
      <Container
        maxWidth={'xl'}
        className={globalStyles.container}
      >
        <Box pt={2} display="flex" alignItems="center" gap={2}>
          <Avatar
            src={product.avatar?.medium ?? undefined}
            sx={{ width: 40, height: 40, borderRadius: '6px', bgcolor: colors.avatarBg, flexShrink: 0 }}
          />
          <Box flex={1} minWidth={0}>
            <PageHeader title={product.name ?? 'Store Product'} />
          </Box>
        </Box>

        <Box mt={3}>
          <Section
            title="Store Product Details"
            content={<Fields fields={storeFields} />}
          />
        </Box>

        <Box mt={3} mb={3}>
          <Section
            title="Product"
            buttons={[
              {
                icon: <CallMadeIcon sx={{ fontSize: 20, color: colors.primary }} />,
                title: 'Open product',
                onClick: () => window.open(`/products/all-products/${product.id}`, '_blank', 'noopener,noreferrer'),
              },
            ]}
            content={
              <>
                <Fields fields={productFields} />
                <Grid size={12}>
                  <Divider />
                </Grid>
                {/* Side by side on a wide screen, so the two read against each other. */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProductInfoItem label="English Description" value={product.descriptions?.en} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProductInfoItem label="Arabic Description" value={product.descriptions?.ar} />
                </Grid>
              </>
            }
          />
        </Box>
      </Container>

      {/* Sila on this store's own listing, so the price she quotes is this store's. */}
      <ChatWidget
        chatUrl={`store-products/${storeProductId}/chat`}
        productName={product.name}
      />
    </>
  );
}
